import { useEffect, useState } from "react";
import { useAtom } from "jotai";
import { useLocation } from "react-router-dom";
import axios from "../../axios";
import { personalData } from "../../states/personalData";
import "./TutorTermsGate.css";

const TERMS_AND_CONDITIONS = [
  {
    title: "Clause 1: Payment & Platform Policy",
    points: [
      "Tutors must not collect payments/monthly tuition fees directly from students or parents. All payments will be handled exclusively by HomeShiksha.",
      "Month 1: Tutors receive 50% of the tuition fee.",
      "From Month 2 onwards: Tutors will get 90% of the tuition fee which will be disbursed by HomeShiksha.",
      "Violations may lead to loss of benefits, account blocking, or legal action.",
    ],
  },
  {
    title: "Clause 2: Demo, Commitment & Attendance",
    points: [
      "Once a demo is scheduled and confirmed, cancellations or changes will not be permitted.",
      "We encourage the tutors to properly check the distance, fees and timings of tuition before going for demo classes. Please apply only after reviewing the tuition details carefully.",
      "A minimum 24-hour prior notice is mandatory for any class cancellation.",
    ],
  },
  {
    title: "Clause 3: Eligibility, Conduct & Legal Terms",
    points: [
      "The tutor confirms Indian citizenship, minimum age of 20 years, and possession of the required qualifications and experience.",
      "Tutors must not share personal contact details, discuss fees with parents, or make independent commitments with parents. Tutors must stay professional. Misconduct can lead to suspension and strict action is liable to be taken against any tutor not adhering the policies.",
      "All communication must be conducted only via the assigned HomeShiksha coordinator.",
      "Uninformed absences or failure to maintain consistency may lead to strict action.",
    ],
  },
  {
    title: "Clause 4: Taxes",
    points: [
      'You acknowledge and agree that you are solely responsible for complying with all applicable provisions of the Income Tax Act, 1961, with respect to income earned from tuition services, including but not limited to declaring such income under the head "Income from Other Sources," and for paying any income tax due thereon as per your applicable tax slab. If your income falls within the threshold where Tax Deducted at Source (TDS) is applicable, it is your responsibility to promptly inform HomeShiksha and provide all necessary documentation required for TDS compliance in accordance with the relevant provisions of the Income Tax Act, 1961.',
      "Further, you acknowledge that you are solely liable for the assessment, collection, payment, and filing of Goods and Services Tax (GST) as required under the Central Goods and Services Tax Act, 2017, and any other applicable GST laws and regulations of the Government of India. You agree to comply with all GST requirements, including timely registration (if applicable), invoicing, payment of GST, and filing of returns as prescribed by law.",
    ],
  },
];

export default function TutorTermsGate() {
  const [personalCenteralData, setPersonalCenteralData] = useAtom(personalData);
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const location = useLocation();

  // Fetch terms status on payment-success page (since dashboard hasn't loaded yet)
  useEffect(() => {
    if (location.pathname === "/payment-success") {
      const jwt = JSON.parse(localStorage.getItem("homeshiksha-tutor-jwt"));
      if (!jwt) return;

      axios
        .post("v1/tutor/tutor-doc-status")
        .then((res) => {
          const { must_accept_terms, terms_accepted_at } = res.data.status;
          setPersonalCenteralData((prev) => ({
            ...prev,
            must_accept_terms,
            terms_accepted_at,
          }));
        })
        .catch((err) => {
          console.error("Failed to fetch terms status:", err);
        });
    }
  }, [location.pathname]);

  const mustAcceptTerms = personalCenteralData.must_accept_terms;

  if (!mustAcceptTerms) {
    return null;
  }

  const handleAcceptTerms = async () => {
    setSubmitting(true);
    try {
      const userId =
        personalCenteralData.id || JSON.parse(localStorage.getItem("userDetails"))?.id;
      await axios.post("/v1/tutor/accept-terms", { userId });
      setPersonalCenteralData((prev) => ({
        ...prev,
        must_accept_terms: false,
        terms_accepted_at: new Date().toISOString(),
      }));
    } catch (error) {
      console.error("Failed to accept terms:", error);
      alert("Failed to accept terms. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="tnc-overlay">
      <div className="tnc-popup">
        <div className="tnc-header">
          <div className="tnc-header-icon">
            <svg
              width="28"
              height="28"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                stroke="#2B93A5"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <h2>Terms & Conditions</h2>
          <p className="tnc-subtitle">
            Please read and accept the following terms to continue as a verified tutor.
          </p>
        </div>

        <div className="tnc-body">
          {TERMS_AND_CONDITIONS.map((clause, idx) => (
            <div key={idx} className="tnc-clause">
              <h3>{clause.title}</h3>
              <ul>
                {clause.points.map((point, pIdx) => (
                  <li key={pIdx}>{point}</li>
                ))}
              </ul>
            </div>
          ))}

          <div className="tnc-closing">
            <p>
              We are committed to supporting you at every step of your teaching journey. Let's work
              together to make learning impactful.
            </p>
            <p className="tnc-regards">
              Warm regards,
              <br />
              <strong>Team HomeShiksha</strong>
            </p>
          </div>
        </div>

        <div className="tnc-footer">
          <label className="tnc-checkbox-label">
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
            />
            <span>I have read, understood, and agree to the above Terms & Conditions.</span>
          </label>
          <button
            className="tnc-accept-btn"
            disabled={!agreed || submitting}
            onClick={handleAcceptTerms}
          >
            {submitting ? "Submitting..." : "Accept & Continue"}
          </button>
        </div>
      </div>
    </div>
  );
}
